import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DataTable,
  Field,
  Input,
  Modal,
  Pager,
  StateBlock,
  useToast,
  type Column,
} from '@w7/ui'
import { useMemo, useState, type FormEvent } from 'react'
import type { AggregateMemberGroupRow, AggregateMemberListItem } from '@/api/cmApi'
import {
  AGGREGATE_MEMBERS_PAGE_SIZE,
  createAggregateMember,
  listAggregateMembers,
  updateAggregateMemberList,
} from '@/api/map/aggregateMembers'
import { useCmMe } from '@/api/hooks/useCmMe'
import {
  canCreateAggregateMembers,
  canEditAggregateMembers,
} from '@/app/staffAccess'
import { CmPanel } from '@/features/catalog/CmPanel'
import '@/features/bet-ticker/bet-ticker.css'
import '@/features/reports/my-pnl.css'
import { formatGroupCreatedAt } from '@/features/security/formatGroupCreatedAt'
import {
  AGGREGATE_CONFIRM_MISMATCH,
  AGGREGATE_PASSWORD_POLICY_LINES,
  aggregateConfirmPasswordErrors,
  aggregatePasswordFormValid,
  aggregatePasswordInvalid,
} from './aggregatePasswordPolicy'
import { AggregateMemberPicker } from './AggregateMemberPicker'

/**
 * Lab `/aggregateMembers/` — Hierarchy rail.
 * CM-API: getAll / create / updateAggregateMemberList + searchHierarchyPage picker.
 */
export function AggregateMembersPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const allowCreate = canCreateAggregateMembers(me.data?.caller)
  const allowEdit = canEditAggregateMembers(me.data?.caller)

  const [page, setPage] = useState(1)
  const [groupNameDraft, setGroupNameDraft] = useState('')
  const [appliedGroupName, setAppliedGroupName] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AggregateMemberGroupRow | null>(null)
  const [groupName, setGroupName] = useState('')
  const [members, setMembers] = useState<AggregateMemberListItem[]>([])
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [confirmTouched, setConfirmTouched] = useState(false)

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['cm', 'aggregate-members', 'list'] })

  const listQ = useQuery({
    queryKey: ['cm', 'aggregate-members', 'list', page, appliedGroupName],
    queryFn: () =>
      listAggregateMembers({
        page,
        ...(appliedGroupName ? { groupName: appliedGroupName } : {}),
      }),
  })

  const pageSize = listQ.data?.pageSize || AGGREGATE_MEMBERS_PAGE_SIZE
  const rows = listQ.data?.rows ?? []
  const total = listQ.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1)

  const resetForm = () => {
    setEditing(null)
    setGroupName('')
    setMembers([])
    setPassword('')
    setConfirmPassword('')
    setPasswordTouched(false)
    setConfirmTouched(false)
  }

  const openCreate = () => {
    resetForm()
    setModalOpen(true)
  }

  const openEdit = (row: AggregateMemberGroupRow) => {
    setEditing(row)
    setGroupName(row.groupName)
    setMembers(row.memberList ?? [])
    setPassword('')
    setConfirmPassword('')
    setPasswordTouched(false)
    setConfirmTouched(false)
    setModalOpen(true)
  }

  const saveM = useMutation({
    mutationFn: async () => {
      if (!members.length) throw new Error('Please select at least one member')
      if (!groupName.trim() && !editing) throw new Error('Group Name is required.')
      if (
        !aggregatePasswordFormValid({
          password,
          confirmPassword,
          editing: Boolean(editing),
        })
      ) {
        throw new Error(AGGREGATE_CONFIRM_MISMATCH)
      }
      if (!editing) {
        return createAggregateMember({
          groupName: groupName.trim(),
          memberList: members,
          password,
          confirmPassword,
        })
      }
      const body: {
        _id: string
        groupName: string
        memberList: AggregateMemberListItem[]
        password?: string
        confirmPassword?: string
      } = {
        _id: editing._id,
        groupName: groupName.trim() || editing.groupName,
        memberList: members,
      }
      if (password && confirmPassword) {
        body.password = password
        body.confirmPassword = confirmPassword
      }
      return updateAggregateMemberList(body)
    },
    onSuccess: async (message) => {
      toast.success(message)
      setModalOpen(false)
      resetForm()
      await invalidate()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const columns = useMemo((): Column<AggregateMemberGroupRow>[] => {
    return [
      {
        id: 'n',
        header: 'SL No',
        width: 56,
        numeric: true,
        cell: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      {
        id: 'created',
        header: 'Created Date',
        cell: (r) => formatGroupCreatedAt(r.createdAt),
      },
      {
        id: 'updated',
        header: 'Updated Date',
        cell: (r) => formatGroupCreatedAt(r.updatedAt),
      },
      { id: 'name', header: 'Group Name', cell: (r) => r.groupName || '—' },
      {
        id: 'members',
        header: 'Members',
        cell: (r) =>
          r.memberList?.length ? (
            <ul className="cm-group-member-chips" aria-label={`Members of ${r.groupName}`}>
              {r.memberList.map((m) => (
                <li key={m._id}>
                  <span className="cm-group-member-chip cm-group-member-chip--static">
                    {m.name}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            '—'
          ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (r) =>
          allowEdit ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              className="cm-aggregate-edit-btn"
              aria-label={`Edit group ${r.groupName}`}
              onClick={() => openEdit(r)}
            >
              Edit
            </Button>
          ) : (
            '—'
          ),
      },
    ]
  }, [page, pageSize, allowEdit])

  const passwordRequired = !editing
  const editingMode = Boolean(editing)
  const passwordFieldInvalid = aggregatePasswordInvalid(password, { editing: editingMode })
  const confirmErrors = aggregateConfirmPasswordErrors(password, confirmPassword, {
    editing: editingMode,
  })
  const canSave =
    members.length > 0 &&
    (editing ? Boolean(groupName.trim() || editing.groupName) : Boolean(groupName.trim())) &&
    aggregatePasswordFormValid({
      password,
      confirmPassword,
      editing: editingMode,
    })

  /** Stacked block — `.w7-field-error` is a flex row; `<br/>` spans lay out sideways. */
  const passwordError =
    passwordTouched && passwordFieldInvalid ? (
      <span className="cm-aggregate-field-error-stack">
        {AGGREGATE_PASSWORD_POLICY_LINES.map((line) => (
          <span key={line} className="cm-aggregate-field-error-line">
            {line}
          </span>
        ))}
      </span>
    ) : undefined

  const confirmError =
    confirmTouched && confirmErrors.length > 0 ? (
      <span className="cm-aggregate-field-error-stack">
        {confirmErrors.map((msg) => (
          <span key={msg} className="cm-aggregate-field-error-line">
            {msg}
          </span>
        ))}
      </span>
    ) : undefined

  const markPasswordTouched = () => {
    setPasswordTouched(true)
    setConfirmTouched(true)
  }

  const loading = listQ.isLoading || (listQ.isFetching && !rows.length)
  const narrowed = Boolean(appliedGroupName)

  return (
    <CmPanel
      title="Aggregate Members"
      subtitle="Create and manage Aggregate console groups"
      wide
      actions={
        allowCreate ? (
          <Button type="button" variant="primary" onClick={openCreate}>
            Create Group
          </Button>
        ) : undefined
      }
    >
      <div className="cm-my-pnl">
        <form
          className="cm-my-pnl-filters"
          role="region"
          aria-label="Aggregate Members filters"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            setPage(1)
            setAppliedGroupName(groupNameDraft.trim())
          }}
        >
          <Field label="Group Name">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={groupNameDraft}
                onChange={(e) => setGroupNameDraft(e.target.value)}
                placeholder="Group Name"
              />
            )}
          </Field>
          <div className="cm-my-pnl-filters-actions">
            <Button type="submit" variant="primary" busy={listQ.isFetching}>
              Search
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon="close"
              disabled={!groupNameDraft && !appliedGroupName}
              onClick={() => {
                setGroupNameDraft('')
                setAppliedGroupName('')
                setPage(1)
              }}
            >
              Clear search
            </Button>
          </div>
        </form>

        {listQ.isError ? (
          <div className="w7-bm-empty-panel" role="alert">
            <StateBlock icon="search" title="Could not load Aggregate Members">
              {(listQ.error as Error).message}
            </StateBlock>
          </div>
        ) : loading ? (
          <div className="w7-bm-results">
            <div className="w7-bm-table-wrap">
              <DataTable
                label="Aggregate Members list"
                rows={[]}
                columns={columns}
                rowKey={(row) => row._id}
                density="compact"
                stickyOffset={0}
                loading
                empty={null}
              />
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="w7-bm-empty-panel">
            <StateBlock
              icon={narrowed ? 'filter' : 'search'}
              title={narrowed ? 'No Data Found' : 'No Aggregate groups yet'}
            >
              {narrowed
                ? `No aggregate groups match Group Name “${appliedGroupName}”.`
                : 'Create a group to manage Aggregate console members.'}
            </StateBlock>
          </div>
        ) : (
          <div className="w7-bm-results">
            <div className="w7-bm-table-wrap">
              <DataTable
                label="Aggregate Members list"
                density="compact"
                stickyOffset={0}
                rows={rows}
                columns={columns}
                rowKey={(row) => row._id}
                empty={null}
              />
            </div>
            {total > 0 ? (
              <div className="cm-security-pager">
                <Pager
                  page={page}
                  pageCount={pageCount}
                  total={total}
                  pageSize={pageSize}
                  onChange={setPage}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => {
          if (saveM.isPending) return
          setModalOpen(false)
          resetForm()
        }}
        title={editing ? 'Edit Group' : 'Add Group'}
        closeDisabled={saveM.isPending}
        footer={
          <div className="cm-form-actions">
            <Button
              type="button"
              variant="primary"
              busy={saveM.isPending}
              disabled={!canSave}
              onClick={() => {
                markPasswordTouched()
                if (!canSave) return
                saveM.mutate()
              }}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saveM.isPending}
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Close
            </Button>
          </div>
        }
      >
        <div className="cm-form">
          <Field label="Group Name" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                required
                maxLength={100}
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter Group Name"
              />
            )}
          </Field>
          <AggregateMemberPicker
            value={members}
            onChange={setMembers}
            disabled={saveM.isPending}
          />
          <Field
            className="cm-aggregate-pw-field"
            label="Password"
            required={passwordRequired}
            error={passwordError}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="password"
                autoComplete="new-password"
                maxLength={20}
                required={passwordRequired}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setPasswordTouched(true)}
                placeholder={editing ? 'Enter new password' : 'Enter Password'}
              />
            )}
          </Field>
          <Field
            className="cm-aggregate-pw-field"
            label="Confirm Password"
            required={passwordRequired || Boolean(password)}
            error={confirmError}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="password"
                autoComplete="new-password"
                maxLength={20}
                required={passwordRequired || Boolean(password)}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setConfirmTouched(true)}
                placeholder={editing ? 'Confirm new password' : 'Enter Confirm Password'}
              />
            )}
          </Field>
          {editing ? (
            <p className="cm-hint">Leave password blank to keep the current password.</p>
          ) : null}
        </div>
      </Modal>
    </CmPanel>
  )
}
